import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import { publicRoutes } from '@constants';
import { type CurrentUser } from '@types';
import useAuthApi from '../api/useAuthApi.ts';
import { getUser } from '@services/edge/users/getUser.ts';
import createSessionUser from '@features/auth/utils/createSessionUser.ts';
import { buildAppUrl } from '@constants/buildAppUrl.ts';
import {
  setDatadogUser,
  clearDatadogUser,
} from '@lib/datadog/datadogUser.ts';

const TENDRIL_ORG_ID = '60d29e6333f477381b0293a0';
const authChannel =
  typeof window !== 'undefined' ? new BroadcastChannel('auth_channel') : null;

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  if (typeof atob !== 'function') {
    return null;
  }

  const tokenParts = token.split('.');
  if (tokenParts.length < 2) {
    return null;
  }

  try {
    const payload = tokenParts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(tokenParts[1].length / 4) * 4, '=');
    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

export const isJwtTokenExpired = (token: string | null): boolean => {
  if (typeof token !== 'string' || token === '') {
    return true;
  }

  const payload = decodeJwtPayload(token);
  const exp = payload?.exp;

  if (typeof exp !== 'number') {
    return true;
  }

  return Date.now() >= exp * 1000;
};

interface SessionStore {
  authToken: string | null;
  secondaryAuthToken: string | null;
  currentUser: CurrentUser | null;
  tabId: string;
  isSessionRefreshing: boolean;
  loading: boolean;
  errorRedirect: boolean;
  authError: string | null;
  startSession: (credentials: {
    email: string;
    password: string;
  }) => Promise<void>;
  endSession: () => Promise<void>;
  clearSession: () => void;
  refreshSession: () => Promise<boolean>;
  checkSessionValidity: () => () => void;
  setCurrentUser: (user: CurrentUser) => Promise<void>;
  clearCurrentUser: () => void;
  retrieveUserFromStorage: () => Promise<void>;
  setAuthError: (message: string | null) => void;
  setSecondaryAuthToken: (token: string | null) => void;
}

export const getTypes = (user: CurrentUser): { isAdmin: boolean; isSdr: boolean; isAgent: boolean } => {
  const roles = Array.isArray(user.type) ? user.type : [];
  return {
    isAdmin: roles.includes('admin'),
    isSdr:   roles.includes('sdr'),
    isAgent: roles.includes('agent'),
  };
};

export const useSessionStore = create<SessionStore>()(
  persist(
    (set, get) => {
      const { login, authLogin, logout, refresh, getMe } = useAuthApi();
      const tabId = uuidv4();
      let lastRefreshTime = 0;

      const safeRedirect = (url: string): void => {
        try {
          window.location.replace(url);
        } catch {
          set({ errorRedirect: true });
        }
      };

      const setCurrentUser = async (user: CurrentUser): Promise<void> => {
        if (Object.keys(user).length === 0) {
          return;
        }

        const types = getTypes(user);
        const userContext = { ...user, ...types };

        const { data: userData, success: userSuccess } = await getUser(
          user.userId,
        );

        if (userSuccess && userData?.profilePicture != null) {
          userContext.avatar = userData.profilePicture;
        }

        if (types.isAgent && user.sdrId != null) {
          const response = await getUser(user.sdrId);
          if (response.data?.name != null) {
            userContext.sdrName = response.data.name;
          }
        }

        userContext.isTendrilUser = userData?.organizationId === TENDRIL_ORG_ID;

        set({ currentUser: userContext });
        setDatadogUser(userContext);
      };

      const clearCurrentUser = (): void => {
        localStorage.removeItem('contextUser');
        clearDatadogUser();
        set({ currentUser: null });
      };

      const retrieveUserFromStorage = async (): Promise<void> => {
        const token = get().authToken;
        if (token == null) {
          clearCurrentUser();
          set({ loading: false });
          return;
        }

        const storedUser = localStorage.getItem('contextUser');
        if (storedUser != null) {
          const parsedUser = JSON.parse(storedUser) as CurrentUser;
          await setCurrentUser(parsedUser);
        }

        set({ loading: false });
      };

      const startSession = async ({
        email,
        password,
      }: {
        email: string;
        password: string;
      }): Promise<void> => {
        set({ authError: null });

        const {
          success: authSuccess,
          data: authData,
          error: authError,
        } = await authLogin({ email, password });

        if (authSuccess && authData?.access_token != null) {
          set({ secondaryAuthToken: authData.access_token });
        } else {
          const errorMsg =
            authError?.message ?? 'Unknown secondary token error';
          set({ authError: errorMsg });
          throw new Error(errorMsg);
        }

        const { success, data, error } = await login({ email, password });

        if (success && data?.access_token != null) {
          const token = data.access_token;
          set({ authToken: token });
          const user = createSessionUser(token);
          await setCurrentUser(user);
        } else {
          const errorMsg = error?.message ?? 'Unknown login error';
          set({ authError: errorMsg });
          throw new Error(errorMsg);
        }
      };

      const clearSession = (): void => {
        clearCurrentUser();
        set({ authToken: null, secondaryAuthToken: null, isSessionRefreshing: false });
      };

      const endSession = async (): Promise<void> => {
        try {
          await logout();
        } catch {
          // Best effort — token may already be expired.
        }
        clearSession();
      };

      const refreshSession = async (): Promise<boolean> => {
        const now = Date.now();

        if (now - lastRefreshTime < 5000) {
          // Debounce — return true to avoid triggering logout from concurrent callers.
          return get().secondaryAuthToken != null;
        }

        lastRefreshTime = now;
        set({ isSessionRefreshing: true });

        const { data: refreshData } = await refresh();

        if (refreshData?.access_token != null) {
          set({
            secondaryAuthToken: refreshData.access_token,
            isSessionRefreshing: false,
          });

          // Validate the new token works but do NOT overwrite currentUser.
          // The auth service /user/me doesn't return user roles (type field),
          // so calling setCurrentUser here would clear the roles set at login
          // from the edge JWT, causing RoleProtectedRoute to redirect to /404.
          const { success: meSuccess } = await getMe();
          return meSuccess;
        }

        set({ isSessionRefreshing: false });
        return false;
      };

      const checkSessionValidity = (): (() => void) => {
        if (authChannel == null) {
          return () => {};
        }

        // Generate unique ID for this tab
        const tabId =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2);

        // Listen messages from other tabs
        authChannel.onmessage = (event: MessageEvent) => {
          const data = event.data as { type: string; tabId: string };

          if (data.type === 'NEW_TAB_OPENED' && data.tabId !== tabId) {
            set({ isSessionRefreshing: true });
            setTimeout(() => {
              set({ isSessionRefreshing: false });
              safeRedirect(buildAppUrl(`${publicRoutes.LOGIN}?reason=tab`));
            }, 500);
          }
        };

        // Notice this tab is new
        authChannel.postMessage({ type: 'NEW_TAB_OPENED', tabId });

        const runSessionCheck = async (): Promise<void> => {
          try {
            if (window.location.pathname.includes('/login')) {
              return;
            }

            const { authToken, secondaryAuthToken } = get();
            if (authToken == null || secondaryAuthToken == null) {
              clearSession();
              safeRedirect(buildAppUrl(`${publicRoutes.LOGIN}?reason=token`));
              return;
            }

            if (isJwtTokenExpired(authToken)) {
              clearSession();
              safeRedirect(
                buildAppUrl(`${publicRoutes.LOGIN}?reason=edge-token-expired`),
              );
              return;
            }

            if (isJwtTokenExpired(secondaryAuthToken)) {
              const refreshed = await get().refreshSession();
              if (!refreshed) {
                clearSession();
                safeRedirect(buildAppUrl(`${publicRoutes.LOGIN}?reason=refresh`));
              }
              return;
            }

            const { success: meSuccess } = await getMe();

            if (!meSuccess) {
              const refreshed = await get().refreshSession();
              if (!refreshed) {
                clearSession();
                safeRedirect(
                  buildAppUrl(`${publicRoutes.LOGIN}?reason=refresh`),
                );
                return;
              }

              const { success: meRetrySuccess } = await getMe();
              if (!meRetrySuccess) {
                clearSession();
                safeRedirect(
                  buildAppUrl(`${publicRoutes.LOGIN}?reason=refresh`),
                );
              }
            }
          } catch (err) {
            console.error('[session-check] unexpected error:', err);
          }
        };

        const interval = setInterval(() => {
          void runSessionCheck();
        }, 10000);

        // Check session immediately when user returns to the tab.
        // Browsers throttle setInterval in background tabs, so this ensures
        // expired tokens are caught as soon as the user comes back.
        const onVisibilityChange = (): void => {
          if (document.visibilityState === 'visible') {
            void runSessionCheck();
          }
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
          clearInterval(interval);
          document.removeEventListener('visibilitychange', onVisibilityChange);
        };
      };

      return {
        authToken: null,
        secondaryAuthToken: null,
        currentUser: null,
        tabId,
        isSessionRefreshing: false,
        loading: true,
        errorRedirect: false,
        authError: null,
        setAuthError: (message) => {
          set({ authError: message });
        },
        setSecondaryAuthToken: (token) => {
          set({ secondaryAuthToken: token });
        },
        startSession,
        endSession,
        clearSession,
        refreshSession,
        checkSessionValidity,
        setCurrentUser,
        clearCurrentUser,
        retrieveUserFromStorage,
      };
    },
    {
      name: 'session-store',
      partialize: (state) => ({
        authToken: state.authToken,
        secondaryAuthToken: state.secondaryAuthToken,
        currentUser: state.currentUser,
      }),
    },
  ),
);
